import { useRouter } from 'next/router';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { useMountedState } from 'react-use';

import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { QueryParamNames } from '~/constants/search';
import { useAccountContext } from '~/providers/AccountProvider';
import { useCreateWorkspace } from '~/swr-hooks/workspaces/useCreateWorkspace';
import { sanitizeEmail } from '~/utils/EmailUtils';
import { withSkipOnboardingCreateLock } from '~/utils/skipOnboardingCreateLock';

export type UseCreateSkipOnboardingWorkspaceParams = {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  workspaceName: string;
};

export const useCreateSkipOnboardingWorkspace = ({
  setIsLoading,
  workspaceName,
}: UseCreateSkipOnboardingWorkspaceParams) => {
  const router = useRouter();
  const { data: account } = useAccountContext();
  const { createWorkspace } = useCreateWorkspace();
  const { showToast } = useToasts();
  const isMounted = useMountedState();

  const createSkipOnboardingWorkspace = useCallback(async () => {
    if (!account) {
      return;
    }

    try {
      // Hold the cross-tab lock only for Workspaces.create. Including router.replace in the
      // lock kept it held when navigation stalled (common in Cypress), so the next test's
      // skip-onboarding create saw `acquired: false` and never POSTed.
      const lockOutcome = await withSkipOnboardingCreateLock(() =>
        createWorkspace({
          workspaceName,
          email: sanitizeEmail(account.email),
          location: 'in-app',
        }),
      );

      if (!lockOutcome.acquired) {
        setIsLoading(true);
        return;
      }

      const newQuery = { ...router.query };

      delete newQuery[QueryParamNames.skipOnboarding];

      await router.replace(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: false },
      );

      if (isMounted()) {
        setIsLoading(false);
      }
    } catch (error) {
      showToast('There was an error creating this workspace. Please try again later.', { color: 'red' });
      reportErrorToBugsnag({
        error,
        context: 'Failed to create workspace while skipping onboarding',
        metadata: {
          data: {
            skipOnboarding: true,
          },
        },
      });

      if (isMounted()) {
        setIsLoading(false);
      }
    }
  }, [account, createWorkspace, isMounted, router, setIsLoading, showToast, workspaceName]);

  return {
    createSkipOnboardingWorkspace,
  };
};
